import React from "react";
import { Link } from "@/components/LocaleLink";
import { motion } from "framer-motion";
import { Star, ArrowRight, HelpCircle } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { getCompare } from "@/data/indsatser";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const IndsatsCompare = () => {
  const { lang } = useLang();
  const c = getCompare(lang);
  const cols = c.columns;

  const fieldRows = [
    { key: "situation", label: cols.situation },
    { key: "duration", label: cols.duration },
    { key: "intensity", label: cols.intensity },
    { key: "purpose", label: cols.purpose },
    { key: "delivery", label: cols.delivery },
  ];

  return (
    <section className="bg-[#1A2E24]" data-testid="indsatser-compare">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 lg:py-28">
        <div className="max-w-2xl mb-12">
          <Reveal><Eyebrow dark>{cols.product}</Eyebrow></Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-5 font-display text-4xl sm:text-5xl text-white leading-tight">{c.title}</h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-4 text-lg text-[#8EA694] leading-relaxed">{c.intro}</p>
          </Reveal>
        </div>

        {/* DESKTOP TABLE */}
        <Reveal delay={0.1}>
          <div className="hidden lg:block overflow-hidden rounded-3xl border border-white/10">
            <table className="w-full border-collapse text-left" data-testid="compare-table">
              <thead>
                <tr className="bg-white/[0.03]">
                  <th className="sticky left-0 z-10 bg-[#122019] p-5 text-xs uppercase tracking-[0.18em] text-[#8EA694] font-semibold w-[190px]">
                    {cols.product}
                  </th>
                  {c.rows.map((r) => (
                    <th key={r.slug} className="p-5 align-top border-l border-white/10 min-w-[190px]">
                      <div className="flex flex-col gap-1.5">
                        {r.flagship && (
                          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] text-[#C47C5D] font-semibold">
                            <Star className="h-3 w-3 fill-[#C47C5D]" /> {lang === "da" ? "Flagskibsforløb" : "Flagship"}
                          </span>
                        )}
                        <Link to={`/indsatser/${r.slug}`} className="font-display text-xl text-white leading-tight hover:text-[#8EA694] transition-colors duration-300">
                          {r.name}
                        </Link>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {fieldRows.map((fr, i) => (
                  <tr key={fr.key} className={i % 2 ? "bg-white/[0.02]" : ""}>
                    <th className="sticky left-0 z-10 bg-[#122019] p-5 align-top text-sm font-semibold text-[#EAE5D9] border-t border-white/10">
                      {fr.label}
                    </th>
                    {c.rows.map((r) => (
                      <td key={r.slug + fr.key} className="p-5 align-top border-l border-t border-white/10 text-sm text-[#EAE5D9]/85 leading-relaxed">
                        {r[fr.key]}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Reveal>

        {/* MOBILE ACCORDION */}
        <div className="lg:hidden" data-testid="compare-accordion">
          <Accordion type="single" collapsible className="space-y-3">
            {c.rows.map((r) => (
              <AccordionItem key={r.slug} value={r.slug} className="rounded-2xl bg-white/[0.04] border border-white/10 px-5 overflow-hidden">
                <AccordionTrigger className="hover:no-underline py-4 text-left">
                  <div className="flex flex-col items-start gap-1 pr-3">
                    {r.flagship && (
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] text-[#C47C5D] font-semibold">
                        <Star className="h-3 w-3 fill-[#C47C5D]" /> {lang === "da" ? "Flagskibsforløb" : "Flagship"}
                      </span>
                    )}
                    <span className="font-display text-xl text-white">{r.name}</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pb-5">
                  <dl className="space-y-3">
                    {fieldRows.map((fr) => (
                      <div key={fr.key}>
                        <dt className="text-xs uppercase tracking-[0.15em] text-[#8EA694] mb-0.5">{fr.label}</dt>
                        <dd className="text-sm text-[#EAE5D9]/90 leading-relaxed">{r[fr.key]}</dd>
                      </div>
                    ))}
                  </dl>
                  <Link to={`/indsatser/${r.slug}`} className="inline-flex items-center gap-1.5 mt-4 text-sm font-medium text-[#8EA694]">
                    {lang === "da" ? "Se forløbet" : "View pathway"} <ArrowRight className="h-4 w-4" />
                  </Link>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>

        {/* HELP + CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.6 }}
          className="mt-10 rounded-3xl bg-[#EAE5D9] p-8 sm:p-10 flex flex-col md:flex-row md:items-center gap-6 justify-between"
          data-testid="compare-help"
        >
          <div className="flex items-start gap-4 max-w-2xl">
            <span className="flex items-center justify-center h-11 w-11 rounded-full bg-[#2D5A3F] text-white shrink-0">
              <HelpCircle className="h-5 w-5" />
            </span>
            <div>
              <h3 className="font-display text-2xl text-[#1A2E24]">{c.help.title}</h3>
              <p className="mt-2 text-[#3B5245] leading-relaxed">{c.help.text}</p>
            </div>
          </div>
          <div className="flex flex-col gap-3">
            <Link
              to="/find-forloeb"
              className="group inline-flex items-center justify-center gap-2 rounded-full bg-[#2D5A3F] text-white px-7 py-3.5 font-medium hover:bg-[#1A2E24] transition-colors duration-300 whitespace-nowrap"
              data-testid="compare-find-forloeb"
            >
              {lang === "da" ? "Find det rette forløb" : "Find the right pathway"}
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <Link
              to="/kontakt?forloeb=usikker"
              className="group inline-flex items-center justify-center gap-2 rounded-full border border-[#2D5A3F] text-[#2D5A3F] px-7 py-3.5 font-medium hover:bg-[#2D5A3F] hover:text-white transition-colors duration-300 whitespace-nowrap"
              data-testid="compare-help-cta"
            >
              {c.help.cta}
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default IndsatsCompare;
